import { useEffect, useState } from 'react';
import DocumentService from '../../Services/documents.service';
import { FIELD_BOXES_ENABLED } from '../../env';
import { normalizeFieldPositions } from './fieldBoxes';

const CACHE_LIMIT = 200;
const cache = new Map();
const remember = (key, value) => {
    if (cache.size >= CACHE_LIMIT) cache.delete(cache.keys().next().value);
    cache.set(key, value);
};
const cacheKey = (caseId, entityType, documentId, refreshKey) =>
    `${caseId}|${entityType}|${documentId}|${refreshKey ?? ''}`;

/**
 * OCR field boxes of a saved Expense / Income / PaymentAcceptance, or null when there is nothing
 * to show (feature off, new document, manual upload, document replaced, fields edited). Failures
 * are silent: the caller just shows the plain document.
 *
 * @param {object} args
 * @param {string} args.caseId
 * @param {'Expense'|'Income'|'PaymentAcceptance'} args.entityType
 * @param {string} args.documentId  Saved row id (nothing is fetched without it).
 * @param {string} args.token
 * @param {boolean} [args.enabled=true]
 * @param {*} [args.refreshKey]  Change it (e.g. the document URL) to fetch again after a save.
 */
export default function useFieldPositions({ caseId, entityType, documentId, token, enabled = true, refreshKey }) {
    const active = Boolean(FIELD_BOXES_ENABLED && enabled && caseId && entityType && documentId && token);
    const key = active ? cacheKey(caseId, entityType, documentId, refreshKey) : null;
    const [state, setState] = useState(() => ({ key, positions: key && cache.has(key) ? cache.get(key) : null }));

    useEffect(() => {
        if (!key) {
            setState({ key: null, positions: null });
            return undefined;
        }
        if (cache.has(key)) {
            setState({ key, positions: cache.get(key) });
            return undefined;
        }

        const controller = new AbortController();
        setState({ key, positions: null });
        (async () => {
            try {
                const response = await DocumentService.GetFieldPositions(
                    caseId, entityType, documentId, token, controller.signal);
                const positions = response.status === 200 ? normalizeFieldPositions(await response.json()) : null;
                if (response.ok) remember(key, positions);
                if (!controller.signal.aborted) setState({ key, positions });
            } catch {
                if (!controller.signal.aborted) setState({ key, positions: null });
            }
        })();
        return () => controller.abort();
    }, [key, caseId, entityType, documentId, token]);

    return state.key === key ? state.positions : null;
}

/** Test hook: forget fetched payloads. */
export function clearFieldPositionsCache() {
    cache.clear();
}
